import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

export default function AthleteDevelopment() {
  const [user, setUser] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserAndPrograms = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      setUser(user);

      // Programs where user is admin
      const { data: adminPrograms } = await supabase
        .from("programs")
        .select("*")
        .eq("administrator", user.id);

      setPrograms(adminPrograms || []);
      if (adminPrograms?.length > 0) setSelectedProgramId(adminPrograms[0].id);
    };
    fetchUserAndPrograms();
  }, []);

  useEffect(() => {
    if (!selectedProgramId) return;

    const fetchAthletesAndScores = async () => {
      setLoading(true);

      // Fetch followers
      const { data: followers } = await supabase
        .from("user_program_follows")
        .select(`user_id, users(full_name)`)
        .eq("program_id", selectedProgramId);

      // Fetch rostered users
      const { data: rostered } = await supabase
        .from("rostered_users")
        .select(`user_id, users(full_name)`)
        .eq("program_id", selectedProgramId);

      const allFollowers = followers || [];
      const allRostered = rostered || [];

      // Merge unique athletes
      const allAthletes = [...allRostered, ...allFollowers].reduce((acc, curr) => {
        if (!acc.find(a => a.user_id === curr.user_id)) acc.push(curr);
        return acc;
      }, []);

      const athleteIds = allAthletes.map(a => a.user_id);

      // Fetch skill scores
      const { data: skillScores } = await supabase
        .from("user_skill_scores")
        .select("*")
        .in("athlete_id", athleteIds)
        .eq("program_id", selectedProgramId);

      // Fetch combine scores
      const { data: combineScores } = await supabase
        .from("user_combine_score")
        .select("*")
        .in("athlete_id", athleteIds)
        .eq("program_id", selectedProgramId);

      // Attach scores to athletes
      const athletesWithScores = allAthletes.map(a => ({
        ...a,
        skill_scores: skillScores?.filter(s => s.athlete_id === a.user_id) || [],
        combine_score: combineScores?.find(c => c.athlete_id === a.user_id) || null,
      }));

      setAthletes(athletesWithScores);
      setLoading(false);
    };

    fetchAthletesAndScores();
  }, [selectedProgramId]);

  if (loading)
    return (
      <div className="text-gray-400 text-center mt-20">
        Loading athlete data...
      </div>
    );

  // Build chart data
  const chartData = athletes.map(a => {
    const point = { name: a.users.full_name };
    a.skill_scores.forEach(s => {
      point[s.tag] = s.user_score;
    });
    if (a.combine_score) {
      point["Combine Score"] = a.combine_score.user_score;
    }
    return point;
  });

  // Collect all skill/combine metric keys for chart lines
  const metrics = Array.from(
    new Set(
      athletes.flatMap(a => [
        ...a.skill_scores.map(s => s.tag),
        ...(a.combine_score ? ["Combine Score"] : []),
      ])
    )
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6">
      {/* Program Toggle */}
      <div className="flex gap-4 mb-6">
        {programs.map(p => (
          <button
            key={p.id}
            onClick={() => setSelectedProgramId(p.id)}
            className={`px-4 py-2 rounded ${
              selectedProgramId === p.id ? "bg-blue-600" : "bg-gray-700"
            }`}
          >
            {p.name}
          </button>
        ))}
      </div>

      <h1 className="text-2xl font-bold mb-4">Athlete Development Tracker</h1>

      {athletes.length === 0 ? (
        <p className="text-gray-400 italic">No athletes found for this program.</p>
      ) : (
        <ResponsiveContainer width="100%" height={500}>
          <LineChart data={chartData}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            {metrics.map(m => (
              <Line
                key={m}
                type="monotone"
                dataKey={m}
                stroke={`#${Math.floor(Math.random() * 16777215).toString(16)}`}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

